import { useEffect, useState } from "react";
import Radio from "../Radio";
import classNames from "classnames";
import Button from "../Button";
import DateRangePicker from "@/shared/DatePicker/DateRange";
import {
  endOfDay,
  endOfMonth,
  endOfWeek,
  endOfYesterday,
  format,
  startOfDay,
  startOfMonth,
  startOfToday,
  startOfWeek,
  startOfYesterday,
  subMonths,
  subWeeks,
} from "date-fns";
import { camelCase } from "lodash";
import Spinner from "../Spinner";

export const RichDatePickerPanel = (props) => {
  const [type, setType] = useState("allTime");
  const [value, setValue] = useState({});
  const [customValue, setCustomValue] = useState();
  const [customRange, setCustomRange] = useState([null, null]);
  const [singleDate, setSingleDate] = useState(null);

  useEffect(() => {
    if (props.value && !value.id) {
      // Backwards compatible:
      // - older saved values may use `type: "custom"`
      // - new UI uses `customRange` + `singleDate`
      const rawType = props.value.defaultValue?.id || props.value.type;
      const type = rawType === "custom" ? "customRange" : rawType;
      setValue(
        props.value.defaultValue || {
          id: props.value.type,
          value: props.value.value,
        }
      );
      const start = props.value?.value?.start
        ? new Date(props.value.value.start)
        : null;
      const end = props.value?.value?.end ? new Date(props.value.value.end) : null;

      if (type === "customRange") {
        setCustomRange([start, end]);
        if (start && end) {
          armCustomValue({
            id: "customRange",
            type: "customRange",
            start,
            end,
          });
        }
      } else if (type === "singleDate") {
        const resolved = start || null;
        setSingleDate(resolved);
        if (resolved) {
          armCustomValue({
            id: "singleDate",
            type: "singleDate",
            start: resolved,
            end: resolved,
          });
        }
      }
      handleType({ type });
    }
  }, []);

  /**
   * Arms the "saveable" custom value for either customRange or singleDate.
   * Keeps value shape consistent: `{ start, end }`.
   */
  function armCustomValue({ id, type, start, end }) {
    const normalizedStart = startOfDay(start);
    const normalizedEnd = endOfDay(end);

    const title =
      format(normalizedStart, "PP") === format(normalizedEnd, "PP")
        ? format(normalizedStart, "PP")
        : `${format(normalizedStart, "PP")} - ${format(normalizedEnd, "PP")}`;

    setCustomValue({
      id,
      type,
      title,
      value: {
        start: normalizedStart,
        end: normalizedEnd,
      },
    });
  }

  function handleTypeChange(e) {
    setType(e.id);
  }

  function handleType(val) {
    let id = camelCase(val.displayText || val.type);

    if (id === "last24Hours") {
      id = "last24";
    }

    setType(id);
  }

  function handleChange(e) {
    props.onChange && props.onChange(e);
    setValue(e);
  }

  const wrapper = classNames("flex", {
    // "grid-cols-1": type !== "custom",
    // "grid-cols-2 md:grid-cols-3": type === "custom",
  });

  /**
   * Get set ranges
   * Last month, last week, last 24 hours
   *
   * @param {string} id - Must be lastMonth lastWeek last24, today
   * @returns {object} - { id, value: { start, end } }
   */
  function getQuickRanges(e) {
    const typeId = e.id;

    const newDate = new Date();

    switch (typeId) {
      case "lastMonth":
        const lastMonth = subMonths(newDate, 1);
        const start = startOfMonth(lastMonth);
        const end = endOfMonth(start);
        return {
          id: typeId,
          value: {
            start,
            end,
          },
        };
      case "thisMonth":
        const stM = startOfMonth(newDate);
        const endM = endOfMonth(newDate);
        return {
          id: typeId,
          value: {
            start: stM,
            end: endM,
          },
        };
      case "lastWeek":
        const lastWeek = subWeeks(newDate, 1);
        const startWeek = startOfWeek(lastWeek);
        const endOfLastWeek = endOfWeek(startWeek);

        return {
          id: typeId,
          value: { start: startWeek, end: endOfLastWeek },
        };
      case "thisWeek":
        const stW = startOfWeek(newDate);
        const endW = endOfWeek(newDate);

        return {
          id: typeId,
          value: { start: stW, end: endW },
        };
      case "yesterday":
        return {
          id: typeId,
          value: { start: startOfYesterday(), end: endOfYesterday() },
        };
      case "today":
        return {
          id: typeId,
          value: { start: startOfToday(), end: endOfDay(newDate) },
        };
      default:
        return { id: typeId, value: null };
    }
  }

  function handleQuickRanges(e) {
    const ranges = getQuickRanges(e);
    handleTypeChange(ranges);
    handleChange({ type: e.id, ...e, ...ranges });
  }

  /**
   * Custom range selection.
   *
   * This is used by index page table filters. Those filters already expect
   * a value shape of `{ start, end }`, and `useFetchAll` already knows how to
   * translate that into `startDate/endDate` query params.
   *
   * @param {[Date|null, Date|null]} range
   */
  function handleCustomRange(range) {
    const [start, end] = Array.isArray(range) ? range : [null, null];
    setCustomRange([start, end]);

    // Only arm a "saveable" value once both ends are chosen.
    if (!start || !end) {
      setCustomValue(undefined);
      return;
    }

    armCustomValue({ id: "customRange", type: "customRange", start, end });
  }

  /**
   * Single date selection.
   * `DateRangePicker` calls `onChange([date, null])` when `selectsRange={false}`.
   */
  function handleSingleDateChange(range) {
    const nextDate = Array.isArray(range) ? range[0] : range;
    setSingleDate(nextDate || null);

    if (!nextDate) {
      setCustomValue(undefined);
      return;
    }

    armCustomValue({
      id: "singleDate",
      type: "singleDate",
      start: nextDate,
      end: nextDate,
    });
  }

  function handleSubmit() {
    handleChange(customValue);
    props.close();
  }

  return (
    <div className={wrapper}>
      {!value.id ? (
        <Spinner />
      ) : (
        <>
          <div className="px-4 py-2">
            <Radio
              id="allTime"
              checked={type === "allTime"}
              onChange={(e) =>
                handleQuickRanges({ id: e.id, title: "All Time" })
              }
              label="All Time"
              size="xs"
            />
            <Radio
              id="today"
              checked={type === "today"}
              onChange={(e) => handleQuickRanges({ id: e.id, title: "Today" })}
              label="Today"
              size="xs"
            />
            <Radio
              id="yesterday"
              checked={type === "yesterday"}
              onChange={(e) =>
                handleQuickRanges({ id: e.id, title: "Yesterday" })
              }
              label="Yesterday"
              size="xs"
            />
            <Radio
              id="thisWeek"
              checked={type === "thisWeek"}
              onChange={(e) =>
                handleQuickRanges({ id: e.id, title: "This Week" })
              }
              label="This Week"
              size="xs"
            />
            <Radio
              id="lastWeek"
              checked={type === "lastWeek"}
              onChange={(e) =>
                handleQuickRanges({ id: e.id, title: "Last Week" })
              }
              label="Last Week"
              size="xs"
            />
            <Radio
              id="thisMonth"
              checked={type === "thisMonth"}
              onChange={(e) =>
                handleQuickRanges({ id: e.id, title: "This Month" })
              }
              label="This Month"
              size="xs"
            />
            <Radio
              id="lastMonth"
              checked={type === "lastMonth"}
              onChange={(e) =>
                handleQuickRanges({ id: e.id, title: "Last Month" })
              }
              label="Last Month"
              size="xs"
            />
            <Radio
              id="customRange"
              checked={type === "customRange"}
              onChange={handleTypeChange}
              label="Custom Range"
              size="xs"
            />
            <Radio
              id="singleDate"
              checked={type === "singleDate"}
              onChange={handleTypeChange}
              label="Single Date"
              size="xs"
            />
          </div>
          {type === "customRange" && (
            <>
              <div className="px-4 py-2">
                <div className="w-full">
                  <DateRangePicker
                    startDate={customRange[0]}
                    endDate={customRange[1]}
                    onChange={handleCustomRange}
                    selectsRange={true}
                    monthsShown={1}
                  />
                </div>
                <div className="flex mt-2 space-x-2 pt-1">
                  <Button
                    size="xs"
                    className="w-full"
                    type="light"
                    onClick={props.close}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="xs"
                    className="w-full"
                    disabled={!customValue}
                    onClick={handleSubmit}
                  >
                    Save
                  </Button>
                </div>
              </div>
              {/* <div>Time</div> */}
            </>
          )}
          {type === "singleDate" && (
            <>
              <div className="px-4 py-2">
                <div className="w-full">
                  <DateRangePicker
                    startDate={singleDate}
                    endDate={null}
                    onChange={handleSingleDateChange}
                    selectsRange={false}
                    monthsShown={1}
                  />
                </div>
                <div className="flex mt-2 space-x-2 pt-1">
                  <Button
                    size="xs"
                    className="w-full"
                    type="light"
                    onClick={props.close}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="xs"
                    className="w-full"
                    disabled={!customValue}
                    onClick={handleSubmit}
                  >
                    Save
                  </Button>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};
